import {
  type Color,
  type DrawingStyle,
  type DrawingStyleExpression,
  type ImageAnnotation,
  type Rectangle,
  ShapeType,
} from '@annotorious/react';
import { assert } from '@repo/shared';

import {
  PatchDetectionBodyLabel,
  type WorkspaceDetectionsResponseOutputItemsItem,
  WorkspaceDetectionsResponseOutputItemsItemLabel as Label,
  type WorkspaceDetectionsResponseOutputItemsItemLabel,
  WorkspaceDetectionsResponseOutputItemsItemSource as Source,
  type WorkspaceDetectionsResponseOutputItemsItemSource,
} from '@/api/generated';

export type Detection = WorkspaceDetectionsResponseOutputItemsItem;
export type DetectionLabel = WorkspaceDetectionsResponseOutputItemsItemLabel;
export type DetectionSource = WorkspaceDetectionsResponseOutputItemsItemSource;

/**
 * Metadata we stash on each annotation's `properties` bag so the style
 * function, predictions list and carousel can read detection state without a
 * separate lookup. Annotorious preserves `properties` across move/resize edits.
 */
export interface AnnotationProperties {
  label: DetectionLabel;
  source: DetectionSource;
  temp: boolean; // `true` for a freshly drawn box that has no backend detection yet.
  confidence?: number;
  skuMetadataId?: string | null;
}

/**
 * Box colors aligned with the label CSS variables in `index.css` (badges, crop
 * card borders): annotated → green, unannotated (Unilever) → blue,
 * removed → grey, missing sku → white. Red is reserved for the selection
 * highlight. Annotorious `DrawingStyle` takes CSS color strings, not Tailwind
 * classes.
 */
export const LABEL_COLORS: Record<DetectionLabel, Color> = {
  [Label.ANNOTATED]: '#22c55e',
  [Label.REMOVED]: '#6b7280',
  [Label.UNANNOTATED]: '#3b82f6',
  [Label.MISSING_SKU]: '#ffffff',
};

/**
 * Pure red highlight for the selected detection. Mirrors `--selected` in
 * `index.css`; keep the two in sync so the canvas box and the crop cards
 * highlight identically.
 */
export const SELECTED_COLOR: Color = '#FF0000';

/** Reads the detection metadata we stored on an annotation, with safe defaults. */
export const getAnnotationProperties = (
  annotation: ImageAnnotation
): AnnotationProperties => {
  const props = annotation.properties;

  return {
    label: props?.label ?? Label.UNANNOTATED,
    source: props?.source ?? Source.USER,
    temp: props?.temp ?? true,
    confidence: props?.confidence,
    skuMetadataId: props?.skuMetadataId,
  };
};

/** A bounding box in natural image pixels, ready for CSS cropping. */
export interface AnnotationRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Reads an annotation's bounding box (natural image pixels) from its rectangle
 * selector. Annotorious normalizes a single selector to an object, but tolerate
 * an array form too so user-drawn boxes keep working.
 */
export const getAnnotationRect = (
  annotation: ImageAnnotation
): AnnotationRect => {
  const { selector } = annotation.target;
  const rectangle = (Array.isArray(selector) ? selector[0] : selector) as
    | Rectangle
    | undefined;
  const geometry = rectangle?.geometry;

  if (!geometry) return { x: 0, y: 0, w: 0, h: 0 };

  const { minX, minY, maxX, maxY } = geometry.bounds;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
};

/**
 * Data-driven style: color each box by its detection label, and highlight the
 * selected boxes in pure red with a thicker stroke so the active detections
 * stand out on the shelf image (PRD 03 FR-6).
 *
 * Selection is derived from the workspace's `selectedIds`, not from
 * Annotorious' render-state `selected` flag: hover-triggered repaints
 * re-evaluate the style with that flag unset, which made the highlight vanish
 * as soon as the pointer moved. Keying off `selectedIds` keeps the red
 * highlight persistent until the box leaves the selection set (the annotator
 * repaints on every new style expression identity).
 */
export const makeAnnotationStyle = (
  selectedIds: ReadonlyArray<string>
): DrawingStyleExpression<ImageAnnotation> => {
  const selectedSet = new Set(selectedIds);

  return (annotation, state): DrawingStyle => {
    const { label } = getAnnotationProperties(annotation);
    const color = LABEL_COLORS[label];
    const selected =
      (state?.selected ?? false) || selectedSet.has(annotation.id);

    return {
      fill: color,
      fillOpacity: 0,
      stroke: selected ? SELECTED_COLOR : color,
      strokeWidth: selected ? 4 : 2,
    };
  };
};

/**
 * Converts an API detection into an Annotorious rectangle annotation.
 * Geometry maps 1:1: top-left `(x1,y1)` → `(minX,minY)`, bottom-right
 * `(x2,y2)` → `(maxX,maxY)`. Coordinates are in natural image pixels;
 * Annotorious scales them to the rendered image automatically.
 */
export const detectionToAnnotation = (
  detection: Detection
): ImageAnnotation => {
  const minX = Math.min(detection.x1, detection.x2);
  const minY = Math.min(detection.y1, detection.y2);
  const maxX = Math.max(detection.x1, detection.x2);
  const maxY = Math.max(detection.y1, detection.y2);

  const id = String(detection.id);
  const properties: AnnotationProperties = {
    label: detection.label,
    source: detection.source,
    temp: false,
    confidence: detection.confidence,
    skuMetadataId: detection.skuMetadataId,
  };

  const selector: Rectangle = {
    type: ShapeType.RECTANGLE,
    geometry: {
      bounds: { minX, minY, maxX, maxY },
      x: minX,
      y: minY,
      w: maxX - minX,
      h: maxY - minY,
    },
  };

  return {
    id,
    bodies: [],
    properties,
    target: {
      annotation: id,
      selector,
    },
  };
};

/** Fraction of the average box height used as the row-banding tolerance. */
const ROW_TOLERANCE_RATIO = 0.5;

/**
 * Orders annotations into human reading order (left→right, top→bottom) even when
 * the shelf boxes aren't perfectly aligned in a row.
 *
 * A plain sort by top edge zig-zags across a row because detection noise / shelf
 * tilt means boxes on the same shelf never share an exact Y. Instead we cluster
 * boxes into rows by their vertical center: walking top-to-bottom, a box joins
 * the current row when its center-Y is within a tolerance of the row's running
 * average (so slight drift is "averaged out"), otherwise it starts a new row.
 * The tolerance is derived from the average box height, keeping it scale-invariant.
 * Each row is then sorted left→right and the rows concatenated top→bottom.
 */
export const sortAnnotationsIntoReadingOrder = (
  annotations: Array<ImageAnnotation>
): Array<ImageAnnotation> => {
  const withRect = annotations.map((annotation) => {
    const rect = getAnnotationRect(annotation);
    return {
      annotation,
      centerX: rect.x + rect.w / 2,
      centerY: rect.y + rect.h / 2,
      height: rect.h,
    };
  });

  const avgHeight =
    withRect.reduce((sum, item) => sum + item.height, 0) /
    (withRect.length || 1);
  const tolerance = avgHeight * ROW_TOLERANCE_RATIO;

  // Group into rows by walking top-to-bottom against each row's running mean.
  const byCenterY = [...withRect].sort(
    (left, right) => left.centerY - right.centerY
  );
  const rows: Array<{ items: typeof withRect; sumY: number }> = [];

  for (const item of byCenterY) {
    const currentRow = rows[rows.length - 1];
    const meanY = currentRow ? currentRow.sumY / currentRow.items.length : 0;
    if (currentRow && Math.abs(item.centerY - meanY) <= tolerance) {
      currentRow.items.push(item);
      currentRow.sumY += item.centerY;
    } else {
      rows.push({ items: [item], sumY: item.centerY });
    }
  }

  return rows.flatMap((row) =>
    row.items
      .sort((left, right) => left.centerX - right.centerX)
      .map((item) => item.annotation)
  );
};

export const getNextAnnotation = (
  annotations: Array<ImageAnnotation>,
  currentDetection: string
): ImageAnnotation => {
  const orderedAnnotations = sortAnnotationsIntoReadingOrder(annotations);

  const currentAnnotationIndex = orderedAnnotations.findIndex(
    (annotation) => annotation.id === currentDetection
  );

  // Walk forward in reading order (wrapping past the end) to the next
  // annotation still labelled UNANNOTATED, so the user is taken straight to the
  // next box that needs their attention. Start one past the current box and
  // scan the whole list; if nothing is unannotated we fall back to the plain
  // next box so the function always advances.
  const count = orderedAnnotations.length;
  for (let offset = 1; offset <= count; offset++) {
    const candidate =
      orderedAnnotations[(currentAnnotationIndex + offset) % count];
    if (
      candidate &&
      getAnnotationProperties(candidate).label === Label.UNANNOTATED
    ) {
      return candidate;
    }
  }

  // No unannotated box remains — advance to the immediate next box.
  const nextAnnotation =
    orderedAnnotations[(currentAnnotationIndex + 1) % count];
  assert(nextAnnotation, 'Next annotation must exist');

  return nextAnnotation;
};

// Helper for border classes
const LABEL_BORDER_CLASSES: Record<DetectionLabel, string> = {
  [PatchDetectionBodyLabel.ANNOTATED]:
    'border-4 border-[var(--color-annotated)]',
  [PatchDetectionBodyLabel.UNANNOTATED]:
    'border-4 border-[var(--color-unannotated)]',
  [PatchDetectionBodyLabel.REMOVED]: 'border-4 border-[var(--color-removed)]',
  [PatchDetectionBodyLabel.MISSING_SKU]:
    'border-4 border-[var(--color-unknown)]',
};

export const getAnnotationBorderClasses = (label: DetectionLabel): string =>
  LABEL_BORDER_CLASSES[label] || '';
